import {
  forwardRef,
  TextareaHTMLAttributes,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { cn } from "@repo/ui/lib/utils";
import { useFormField } from "@repo/ui/components/ui/form";

interface FloatingLabelTextareaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  id?: string;
  label: string;
  value?: string;
  ref?: any;
  onBlur?: () => void;
  required?: boolean;
  classes?: string;
}

const FloatingLabelTextarea: React.FC<FloatingLabelTextareaProps> = forwardRef<
  HTMLTextAreaElement,
  FloatingLabelTextareaProps
>(({ id, label, value, onBlur, required, classes, ...props }, ref) => {
  const [isFocused, setIsFocused] = useState(false);
  const formField = useFormField();

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  useImperativeHandle(ref, () => textareaRef.current!);

  const adjustHeight = () => {
    if (textareaRef?.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight + 10}px`;
    }
  };

  return (
    <div className="relative w-full">
      <textarea
        id={id}
        value={value}
        ref={textareaRef}
        onFocus={() => setIsFocused(true)}
        onInput={adjustHeight}
        onBlur={() => {
          if (onBlur) onBlur();
          setIsFocused(false);
        }}
        placeholder=" "
        {...props}
        className={cn(
          "block w-full min-h-[250px] pl-[20px] pr-[20px] pt-[40px] bg-secondary-gray1 dark:bg-secondary-black text-sm font-black text-gray-900 rounded-[10px] focus:outline-none focus:ring-1 focus:ring-secondary-black peer dark:text-primary-black",
          {
            "bg-secondary-gray2 dark:bg-primary-white":
              isFocused || (value && value.trim()),
            "bg-secondary-gray1 dark:bg-secondary-black":
              !isFocused && !(value && value.trim()),
          },
          classes || "",
          {
            "bg-secondary-gray2 dark:bg-primary-white opacity-60":
              props.disabled,
          },
          { "ring-[1.5px] ring-secondary-red": formField.error },
        )}
      />
      <label
        htmlFor={id}
        className={cn(
          "absolute left-[20px] top-[26px] text-sm text-secondary-black dark:text-primary-white font-black duration-150 transform scale-100 origin-[0] peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:scale-75 peer-focus:-translate-y-3",
          {
            "scale-75 -translate-y-3 font-medium dark:text-primary-black":
              props.disabled || isFocused || (value && value.trim()),
          },
          {
            "opacity-60": props.disabled,
          },
        )}
      >
        {label} {required ? "*" : ""}
      </label>
    </div>
  );
});

FloatingLabelTextarea.displayName = "FloatingLabelTextarea";

export { FloatingLabelTextarea };
